<template>
    <div style="overflow-x: auto;">
        <data-table
            :id="`${id}-report`"
            :columns="visible_columns"
            :rows="filtered_rows"
            :search="state.search"
            :total="state.total"
            :found="state.found"
            :current-page="state.page"
            :total-pages="nb_pages"
            :row-class-provider="row_classes"
            :cell-class-provider="cell_classes"
            :searchable="false"
            :loading="loading"
            table-class="uk-table-striped lam-records-list"
            empty-title="Loading..."
            class="uk-margin-top uk-margin-bottom"
            @sort="sort_by"
            @search="search"
            @page="goto_page"
            @click="record_edit('prospective_project', $event.id)"
        >
            <template v-slot:controls><slot name="controls"></slot></template>
            <template
                v-slot:tbody="{ row, field }"
            >
                <router-link
                    v-if="field === 'name'"
                    :to="{ name: 'prospective_project.edit', params: { id: row.id } }"
                >
                    {{ row.name }}
                </router-link>
                <span
                    v-else-if="field === 'computed_prospective_fee'"
                >
                    <span v-if="!!row[field]">$&nbsp;{{ row[field] | nibnut.number("0,0") }}</span>
                </span>
                <span
                    v-else-if="field === 'shelf_life'"
                >
                    <span v-if="(row.project_status && row.project_status.match(/^(chosen\-pending)$/i))">
                        {{ project_status_label(row.project_status) }}
                    </span>
                    <span v-else>{{ row.shelf_life }}</span>
                </span>
                <div
                    v-else-if="field.match(/^(computed_starts_at_as_string|initial_deliverable_at_as_string|proposal_sent_at_as_string)$/i)"
                >
                    {{ row[field] | nibnut.date("MM/YYYY") }}
                </div>
                <div
                    v-else-if="field.match(/^(computed_last_communication_at_as_string|projected_awarded_at_as_string)$/i)"
                >
                    {{ row[field] | nibnut.date("MM/DD/YY") }}
                </div>
                <div
                    v-else-if="field === 'days_since_received'"
                >
                    <span v-if="!!row[field]">{{ row[field] | nibnut.number("0,0") }} days</span>
                </div>
                <div
                    v-else-if="field === 'automated_communication_log_count'"
                >
                    <button
                        v-if="(row.recent_user_note_count + row.automated_communication_log_count) > 0"
                        type="button"
                        :class="{ 'uk-button-primary-w-icon': !!row.automated_communication_log_count && !row.recent_user_note_count, 'uk-button-danger': !!row.recent_user_note_count }"
                        class="uk-button"
                        @click.prevent.stop="display_logs_for_project(row)"
                    >
                        <open-icon glyph="sticky-note" />
                    </button>
                </div>
                <span
                    v-else
                >
                    <span v-if="!!row[field]">{{ row[field] }}</span>
                </span>
            </template>
        </data-table>
        <project-logs-dialog
            :project="viewing_logs_for_project || {}"
            :show.sync="viewing_logs"
        />
    </div>
</template>

<script>
import is_remote_data_table_source from "../mixins/IsRemoteDataTableSource"
import string_utilities from "../mixins/StringUtilities"

import OpenIcon from "./OpenIcon"
import ProjectLogsDialog from "../dialogs/ProjectLogsDialog"

export default {
    name: "ProspectiveProjectsDataTable",
    mixins: [is_remote_data_table_source, string_utilities],
    components: {
        OpenIcon,
        ProjectLogsDialog
    },
    watch: {
        userId: "refresh",
        context: "refresh"
    },
    methods: {
        pre_load () {
            this.set_state_values({
                filter: this.userId,
                context: this.context
            })
        },
        row_classes (row) {
            let old = row.computed_last_communication_at_as_string ? (this.$moment().diff(this.$moment(row.computed_last_communication_at_as_string), "days") >= 30) : false
            if(!old && !!row.projected_awarded_at_as_string) {
                old = (this.$moment().diff(this.$moment(row.projected_awarded_at_as_string), "days") >= 5)
            }
            return { "uk-text-danger": old }
        },
        cell_classes (row, field, column) {
            if(field !== "pm_initials") return { "uk-background-warning": !row[field] }
            return {}
        },
        user_can_be_pic (user) {
            return !!user && ((user.role === this.$root.constants.roles.ASSOCIATE_PRINCIPAL.id) || (user.role === this.$root.constants.roles.PRINCIPAL.id))
        },
        pics_filter (user) {
            return this.user_can_be_pic(user)
        },
        display_logs_for_project (project) {
            this.viewing_logs_for_project = project
            this.viewing_logs = true
        },
        filter_rows (rows) {
            if(this.state.filter_on && this.state.filter) {
                return rows.filter((row) => {
                    if(this.state.filter_on === "pic_or_pm_id") return (!!row.pic_id === !!parseInt(this.state.filter)) || (!!row.pm_id === !!parseInt(this.state.filter))
                    if(this.state.filter_on.match(/_at$/i)) return !!row[this.state.filter_on] === !!parseInt(this.state.filter)
                    return row[this.state.filter_on] === this.state.filter
                })
            }
            return rows
        }
    },
    computed: {
        state_identifier () {
            return `${this.id}-reports`
        },
        visible_columns () {
            const { pic_id, recent_user_note_count, ...visible_columns } = this.columns
            return visible_columns
        },
        can_refresh () {
            return this.is_public_page || this.profile_id
        }
    },
    props: {
        id: {
            type: String,
            default: "prospective_projects"
        },
        userId: {
            type: Number,
            default: 0
        },
        context: {
            type: String,
            default: "reports"
        }
    },
    data () {
        return {
            entity: "project",
            columns: {
                pic_id: { label: "PIC ID", sort: false },
                pic_initials: { label: "PIC", sort: null, type: "plain", head_class: { wrapped: true } },
                pm_initials: { label: "PM", sort: null, type: "plain", head_class: { wrapped: true } },
                client_name: { label: "Architect", sort: false, type: "plain", head_class: { wrapped: true } },
                name: { label: "Name", sort: null, type: "plain", head_class: { wrapped: true }, cell_class: { "lam-col-project-name": true } },
                shelf_life: { label: "Status", sort: false, type: "plain", head_class: { wrapped: true }, cell_class: { "uk-text-nowrap": true } },
                days_since_received: { label: "Age", sort: null, type: "plain", head_class: { "uk-text-right wrapped": true }, cell_class: { "uk-text-right uk-text-nowrap": true } },
                proposal_sent_at_as_string: { label: "Proposal Sent", sort: null, type: "plain", head_class: { "uk-text-center wrapped": true }, cell_class: { "uk-text-center": true } },
                type_of_request_label: { label: "Type of Request", sort: false, type: "plain", head_class: { wrapped: true } },
                computed_prospective_fee: { label: "Fee", sort: null, type: "plain", head_class: { "lam-col-fee uk-text-right wrapped": true }, cell_class: { "uk-text-right": true } },
                projected_awarded_at_as_string: { label: "To be awarded", sort: null, type: "plain", head_class: { "uk-text-center wrapped": true }, cell_class: { "uk-text-center": true } },
                computed_last_communication_at_as_string: { label: "Last Comm.", sort: null, type: "plain", head_class: { "uk-text-center wrapped": true }, cell_class: { "uk-text-center": true } },
                computed_starts_at_as_string: { label: "Anticipated Engagement", sort: null, type: "plain", head_class: { "uk-text-center wrapped": true }, cell_class: { "uk-text-center": true } },
                initial_deliverable_at_as_string: { label: "Initial Deliverable", sort: false, type: "plain", head_class: { "uk-text-center wrapped": true }, cell_class: { "uk-text-center": true } },
                automated_communication_log_count: { label: "Logs", sort: false, head_class: { "uk-text-center": true }, cell_class: { "uk-text-center": true } },
                recent_user_note_count: { label: "Logs", sort: false, head_class: { "uk-text-center": true }, cell_class: { "uk-text-center": true } }
                // notes... Which notes? extenuating circumstances? Other?
            },
            default_state: {
                per_page: 30,
                page: 1,
                sort_by: "days_since_received",
                sort_dir: "asc",
                filter_on: "pic_or_pm_id",
                filter: null,
                archived: false,
                activated_at: 0,
                search: "",
                total: 0,
                found: 0,
                context: "reports"
            },
            viewing_logs_for_project: null,
            viewing_logs: false
        }
    }
}
</script>
